import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Award } from "lucide-react";
import { fetchProject, fetchProjects } from "@/lib/api";

const ROLE_LABEL = {
  director: "Direction",
  producer: "Production",
  writer: "Writing",
  photographer: "Photography",
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProject(null);
    setError(null);
    fetchProject(slug)
      .then(setProject)
      .catch(() => setError("not found"));
    fetchProjects().then((all) => setOthers(all.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (error) {
    return (
      <main className="min-h-screen bg-zinc-950 text-zinc-50 flex flex-col items-center justify-center px-6">
        <p data-testid="project-not-found" className="font-serif text-4xl md:text-6xl italic">Project not found.</p>
        <Link
          to="/work"
          className="mt-8 text-[11px] uppercase tracking-[0.3em] link-underline"
          data-testid="project-back-to-work"
        >
          Back to all work
        </Link>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500 text-sm uppercase tracking-[0.3em]">
        Loading…
      </main>
    );
  }

  return (
    <main data-testid="project-detail-page" className="bg-zinc-950 text-zinc-50">
      {/* Cover */}
      <section className="relative h-[80vh] md:h-screen overflow-hidden">
        <img
          src={project.cover}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/30 via-zinc-950/20 to-zinc-950" />
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 lg:px-16 pb-16 md:pb-24 max-w-[1600px] mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-300 mb-6">
            {ROLE_LABEL[project.role]} · {project.category} · {project.year}
          </p>
          <h1
            data-testid="project-title"
            className="font-serif text-5xl md:text-7xl lg:text-9xl leading-[0.95] tracking-tight"
          >
            {project.title}
          </h1>
        </div>
      </section>

      {/* Synopsis + credits */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-12 gap-y-12 gap-x-12">
        <div className="col-span-12 md:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
            Synopsis
          </p>
          <p
            data-testid="project-synopsis"
            className="font-serif text-2xl md:text-4xl leading-snug text-zinc-100"
          >
            {project.synopsis}
          </p>
        </div>
        <aside className="col-span-12 md:col-span-4 md:col-start-9 border-l border-zinc-900 pl-8 md:pl-12">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
            Credits
          </p>
          <ul className="space-y-4">
            {Object.entries(project.credits || {}).map(([role, name]) => (
              <li key={role}>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  {role}
                </p>
                <p className="text-zinc-100 text-base mt-1">{name}</p>
              </li>
            ))}
          </ul>

          {project.awards?.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-12 mb-6">
                Recognition
              </p>
              <ul className="space-y-3">
                {project.awards.map((a) => (
                  <li key={a} className="flex items-start gap-3 text-zinc-200 text-sm">
                    <Award className="h-4 w-4 mt-0.5 text-zinc-400 shrink-0" strokeWidth={1.5} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      </section>

      {/* Video embed OR external CTA */}
      {project.video_embed ? (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
            Watch
          </p>
          <div
            data-testid="project-video-embed"
            className="aspect-video w-full bg-zinc-900 overflow-hidden border border-zinc-900"
          >
            <iframe
              src={project.video_embed}
              title={`${project.title} trailer`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {project.external_url && (
            <a
              href={project.external_url}
              target="_blank"
              rel="noreferrer"
              data-testid="project-external-link"
              className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-zinc-400 hover:text-white"
            >
              {project.external_label || "Open external link"}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
            </a>
          )}
        </section>
      ) : project.external_url ? (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <a
            href={project.external_url}
            target="_blank"
            rel="noreferrer"
            data-testid="project-external-cta"
            className="group inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors"
          >
            {project.external_label || "Open external link"}
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
          </a>
        </section>
      ) : null}

      {/* Stills gallery */}
      {project.stills?.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
            Stills
          </p>
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {project.stills.map((s, i) => {
              const spans = [
                "col-span-12 md:col-span-8 aspect-[16/10]",
                "col-span-12 md:col-span-4 aspect-[3/4] md:mt-20",
                "col-span-12 md:col-span-6 md:col-start-4 aspect-[4/3]",
                "col-span-12 md:col-span-7 md:col-start-6 aspect-[16/10] md:-mt-16",
              ];
              return (
                <div
                  key={`${s}-${i}`}
                  className={`relative overflow-hidden ${spans[i % spans.length]}`}
                >
                  <img
                    src={s}
                    alt={`${project.title} still ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
                  />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="border-t border-zinc-900 py-16 px-6 md:px-12 lg:px-16 max-w-[1600px] mx-auto flex items-center justify-between">
        <Link
          to="/work"
          data-testid="project-back-link"
          className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-zinc-300 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
          Back to catalogue
        </Link>
      </section>

      {/* Other work */}
      {others.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-10">
            Adjacent work
          </p>
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/work/${p.slug}`}
                data-testid={`project-related-${p.slug}`}
                className="group col-span-12 md:col-span-4 relative block overflow-hidden aspect-[4/5]"
              >
                <img
                  src={p.cover}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/85 via-zinc-950/10 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-300 mb-2">
                    {ROLE_LABEL[p.role]} · {p.year}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl">{p.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
